import axios from 'axios';
import {BASE_URL} from './config';
import LocalStorage from '../storage/LocalStorage';
import {storesProvider} from '../../App';

axios.defaults.withCredentials = true;

const apiClient = axios.create({
  baseURL: BASE_URL,
});

const anonClient = axios.create({
  baseURL: BASE_URL,
});

function isUnauthorizedError(error: any) {
  if (!error || !error?.response || !error?.response?.status) {
    return false;
  }
  const {
    response: {status},
  } = error;
  return status === 401;
}

async function renewToken() {
  const response = await apiClient.post('/authentication/refresh-token', {});
  storesProvider.getNavigationStore().tryGetMyOrganization();
  return response.status === 200;
}

async function withAuth(headers: any) {
  if (!headers) {
    headers = {};
  }
  return headers;
}

let refreshingFunc: any;

apiClient.interceptors.response.use(
  res => ({
    success: true,
    ...res,
    data: res.data,
  }),
  async error => {
    const originalConfig = error.config;
    const storage = new LocalStorage();

    const user = storage.getItemFromStorage(LocalStorage.Keys.USER_KEY);
    if (!isUnauthorizedError(error) || !user) {
      return resolveWithError(error);
    }

    try {
      if (!refreshingFunc) {
        refreshingFunc = renewToken();
      }

      const success = await refreshingFunc;

      if (success) {
        try {
          return await apiClient.request(originalConfig);
        } catch (innerError) {
          return resolveWithError(error);
        }
      } else {
        await logout();
      }
    } catch (err) {
      await logout();
    } finally {
      refreshingFunc = undefined;
    }
  },
);

const logout = async () => {
  const storage = new LocalStorage();
  await anonClient.delete(`/authentication/invalidate_tokens`, {});
  storage.resetThemeColors();
  storage.wipeLocalStorage();
  storesProvider.reInitStores();
  window.location.reload();
};

anonClient.interceptors.response.use(
  res => ({
    success: true,
    ...res,
    data: res.data,
  }),
  async error => {
    return resolveWithError(error);
  },
);

const resolveWithError = (error: any) => {
  if (error?.code === 'ECONNABORTED') {
    return Promise.resolve({
      success: false,
      errors: ['timeout'],
    });
  }
  if (!error.response?.data) {
    return Promise.resolve({
      success: false,
      errors: ['unknown'],
    });
  }
  if (
    error.response.data?.Errors &&
    Array.isArray(error.response.data?.Errors)
  ) {
    return Promise.resolve({
      success: false,
      errors: error.response.data.Errors,
    });
  } else if (error.response.data) {
    return Promise.resolve({
      success: false,
      errors: [error.response.data],
    });
  }
};

export {apiClient, withAuth, anonClient};
